<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { installLegacyUiAdapter } from '../services/legacyUiAdapter';
import { customerAuthSnapshot, customerRequest, restoreCustomer } from '../services/customerSession';
import { installLegacyCustomerSocialAuth } from '../services/customerSocialAuth';
import AreaIndexPage from '../pages/AreaIndexPage.vue';
import MassageAtHomePage from '../pages/MassageAtHomePage.vue';

type Screen = 'map' | 'home' | 'home-massage' | 'area-index' | 'partners' | 'partner-profile' | 'orders' | 'chat' | 'account' | 'auth' | 'saved' | 'store-dashboard' | 'partner-register';
const path = location.pathname;
const pathPartnerSlug = path.match(/^\/(?:shop|doi-tac)\/([^/]+)\/?$/)?.[1] || '';
const areaRoute = path.match(/^\/chuyen-vien-khu-vuc\/([^/]+)\/?$/)?.[1] || '';
let screen: Screen = path.replace(/\/+$/, '') === '/chuyen-vien-khu-vuc' && new URLSearchParams(location.search).get('public') === '1' ? 'area-index'
  : /^\/massage-tai-nha(?:\/[^/]+){0,3}\/?$/.test(path) ? 'home-massage'
  : path.replace(/\/+$/, '') === '/chon-vi-tri' ? 'map'
  : path.startsWith('/chuyen-vien') ? 'partners'
  : path.startsWith('/shop/') || path === '/shop/' ? 'partner-profile'
  : path.startsWith('/don-hang') ? 'orders'
  : path.startsWith('/tin-nhan') ? 'chat'
  : path.startsWith('/da-luu') ? 'saved'
  : path.startsWith('/quan-ly-gian-hang') ? 'store-dashboard'
  : path.startsWith('/tai-khoan') ? 'auth'
  : path.startsWith('/ca-nhan') || path.startsWith('/cua-hang') ? 'account' : 'home';

const routeClass = screen === 'partner-profile'
  ? 'tm-profile-route'
  : screen === 'chat'
    ? 'tm-chat-mode tm-inbox-reference'
    : `tm-${screen}-mode`;
const current = (name: string) => (screen === name ? 'page' : undefined);
const accountHref = ref(screen === 'account' ? '/ca-nhan/' : '/tai-khoan/');
const accountLabel = ref('Tài khoản');
const config = {
  restUrl: `${location.origin}/legacy/`, homeUrl: `${location.origin}/`, assetUrl: `${location.origin}/platform-assets/`,
  screen, loggedIn: false, hasStore: false, nonce: 'node-session', userId: 0, userAvatar: '',
  userDisplayName: '', userPhone: '', r2Enabled: false,
  partnerSlug: new URLSearchParams(location.search).get('partner') || pathPartnerSlug, hideProfileReviewComposer: false,
  groupChatVisible: false,
  oauth: {}, mediaLimits: { image: 5242880, video: 104857600 },
  mapTilesKey: (window as typeof window & { __TIM_MAP_TILES_KEY__?: string }).__TIM_MAP_TILES_KEY__ || import.meta.env.VITE_GOONG_MAP_TILES_KEY || '',
  brand: {
    key: 'massage', name: 'Tìm Massage', tagline: 'Thư giãn hơn, gần bạn hơn',
    primary: '#075e59', accent: '#cc7648',
    logo: `${location.origin}/massage-assets/tim-massage-logo-horizontal.png`,
    hero: `${location.origin}/massage-assets/hero-wellness-v2.jpg`,
    homeHero: { heading: '', description: '' },
    serviceSlugs: ['massage-toan-than','massage-thuy-dien','massage-thai','massage-bau','massage-co-vai-gay','cao-gio','massage-da-nong','giac-hoi','massage-chan','massage-lung','massage-tay','massage-mat','thu-gian-giai-moi-tai-cua-hang','massage-me-va-be','massage-cham-soc-da-co-the','massage-cao-gio','massage-giac-hoi','massage-dau','thu-gian-tan-noi'],
    homeServiceSlugs: ['massage-toan-than','massage-thuy-dien','massage-thai','massage-bau','massage-co-vai-gay','cao-gio','massage-da-nong','giac-hoi','massage-chan','massage-lung','massage-tay','massage-mat','thu-gian-giai-moi-tai-cua-hang','massage-me-va-be','massage-cham-soc-da-co-the','massage-cao-gio','massage-giac-hoi','massage-dau','thu-gian-tan-noi'],
  },
};

// Non-hashed compatibility files need a release key so a new Vue bundle never
// gets paired with stale browser-cached legacy scripts.
const uiAssetVersion = '20261101_resilient_api_1';
const versionAsset = (url: string) => `${url}${url.includes('?') ? '&' : '?'}node_ui=${uiAssetVersion}`;
const surfaceRoute = () => {
  const value = location.pathname.replace(/\/+$/, '') || '/';
  if (value === '/ca-nhan') return '/ca-nhan/' as const;
  if (value === '/cua-hang') return '/cua-hang/' as const;
  return '/quan-ly-gian-hang/' as const;
};
const surfaceTraceId = () => typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `surface-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
const reportSurfaceDiagnostic = (stage: 'session_restore' | 'store_lookup' | 'account_boot' | 'store_boot' | 'render_timeout', code: 'SURFACE_SESSION_UNAVAILABLE' | 'SURFACE_STORE_LOOKUP_FAILED' | 'SURFACE_ASSET_LOAD_FAILED' | 'SURFACE_RENDER_EMPTY' | 'SURFACE_BOOT_TIMEOUT') => {
  // Deliberately send a fixed, non-sensitive payload. Browser exception text
  // can contain addresses or request fragments and must not reach CMS logs.
  void customerRequest('/v1/sites/massage/client-errors', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stage, code, traceId: surfaceTraceId(), route: surfaceRoute() }),
  }, false).catch(() => undefined);
};

const loadScript = (src: string, source: string) => new Promise<void>((resolve, reject) => {
  const script = document.createElement('script');
  script.src = versionAsset(src); script.dataset.source = source;
  // Dynamic scripts are async by default. Disabling async lets the browser
  // download a whole dependency group concurrently while still executing it
  // in insertion order.
  script.async = false;
  script.onload = () => resolve(); script.onerror = () => reject(new Error(`Không tải được ${source}`));
  document.body.append(script);
});
const loadStyle = (href: string) => {
  const versionedHref = versionAsset(href);
  if (document.querySelector(`link[href="${versionedHref}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet'; link.href = versionedHref;
  document.head.append(link);
};
const preloadScript = (src: string) => {
  const href = versionAsset(src);
  if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preload'; link.as = 'script'; link.href = href;
  document.head.append(link);
};
const loadStyleBefore = (href: string, beforeSelector: string) => {
  const versionedHref = versionAsset(href);
  if (document.querySelector(`link[href="${versionedHref}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet'; link.href = versionedHref;
  const before = document.querySelector(beforeSelector);
  if (before?.parentNode) before.parentNode.insertBefore(link, before);
  else document.head.append(link);
};
const loadSeries = async (items: Array<[string,string]>) => {
  await Promise.all(items.map(([src, source]) => loadScript(src, source)));
};
let goongAssetsPromise: Promise<void> | null = null;
// The Goong runtime is large. Do not include it in the document critical path;
// load it only after public content has painted and a map enhancement is needed.
const ensureGoongAssets = () => {
  const runtime = window as typeof window & { goongjs?: unknown; __TIM_MAP_TILES_KEY__?: string };
  config.mapTilesKey = runtime.__TIM_MAP_TILES_KEY__ || config.mapTilesKey;
  if (runtime.goongjs) return Promise.resolve();
  if (!goongAssetsPromise) {
    loadStyle('/platform-assets/goong-js.vendor.css?v=1.0.9-local');
    goongAssetsPromise = loadScript('/platform-assets/goong-js.vendor.js?v=1.0.9-local', 'goong-map')
      .then(() => {
        config.mapTilesKey = (window as typeof window & { __TIM_MAP_TILES_KEY__?: string }).__TIM_MAP_TILES_KEY__ || config.mapTilesKey;
      })
      .catch((error) => {
        goongAssetsPromise = null;
        throw error;
      });
  }
  return goongAssetsPromise;
};
(window as typeof window & { TimEnsureGoong?: () => Promise<void> }).TimEnsureGoong = ensureGoongAssets;
const waitForInitialRender = () => new Promise<void>((resolve, reject) => {
  const started = performance.now();
  const check = () => {
    const target = document.querySelector<HTMLElement>('.tm-screen');
    if (target && !target.hidden && target.childElementCount) resolve();
    else if (performance.now() - started > 5000) reject(new Error(`RENDER_EMPTY:${screen}`));
    else requestAnimationFrame(check);
  };
  check();
});
const releaseSurfaceBoot = () => {
  const appRoot = document.querySelector<HTMLElement>('.tim-makeup-app');
  appRoot?.classList.remove('tm-surface-booting');
  appRoot?.querySelector('.tm-boot-veil')?.remove();
};
const releaseNavigationBoot = () => {
  document.querySelector<HTMLElement>('.tim-makeup-app')?.classList.add('tm-navigation-ready');
};
const normalizeMassageCopy = () => {
  document.querySelectorAll<HTMLElement>('.tm-auth-brand h1,.tm-account-guest h2').forEach((node) => {
    const current = node.textContent || '';
    const next = current.replaceAll('Tìm Makeup','Tìm Massage');
    if (next !== current) node.textContent = next;
  });
  document.querySelectorAll<HTMLElement>('.tm-auth-brand p,.home-free-copy p,.home-free-kicker').forEach((node) => {
    const current = node.innerHTML;
    const next = current.replaceAll('làm đẹp','massage').replaceAll('Tìm Makeup','Tìm Massage');
    if (next !== current) node.innerHTML = next;
  });
  // The shared profile renderer uses a textual cover fallback before a store
  // uploads an image.  Keep that fallback and its profile chat label on the
  // Massage tenant; otherwise an empty cover visibly leaks the old brand.
  document.querySelectorAll<HTMLElement>('.tm-cover-empty,.tm-profile-chat-hint strong').forEach((node) => {
    const current = node.textContent || '';
    const next = current.replaceAll('Tìm Makeup', 'Tìm Massage');
    if (next !== current) node.textContent = next;
  });
};

const installLocationCompatibility = () => {
  const host = window as typeof window & {
    timCurrentPosition?: (
      success: PositionCallback,
      failure?: PositionErrorCallback,
      options?: PositionOptions,
    ) => void;
    TimNativeShell?: { postMessage: (message: string) => void };
  };
  if (typeof host.timCurrentPosition === 'function') return;
  host.timCurrentPosition = (success, failure, options) => {
    let settled = false;
    const unavailable = {
      code: 2,
      message: 'Thiết bị chưa hỗ trợ định vị.',
      PERMISSION_DENIED: 1,
      POSITION_UNAVAILABLE: 2,
      TIMEOUT: 3,
    } as GeolocationPositionError;
    const nativeShell = host.TimNativeShell;
    const requestedTimeout = Number(options?.timeout);
    const timeout = Number.isFinite(requestedTimeout) && requestedTimeout > 0
      ? Math.min(Math.max(requestedTimeout, 18000), 22000)
      : 22000;
    let fallbackTimer: number | undefined;
    const finishSuccess: PositionCallback = (position) => {
      if (settled) return;
      settled = true;
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      success(position);
    };
    const finishFailure: PositionErrorCallback = (error) => {
      if (settled) return;
      settled = true;
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      failure?.(error);
    };
    // A first-party WebView must use the app permission only. Falling back to
    // navigator.geolocation here makes WebKit show a second, website-level
    // permission prompt even when the native app location permission exists.
    if (typeof nativeShell?.postMessage === 'function') {
      const requestId = `location-${crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;
      const onResponse = (event: Event) => {
        const detail = (event as CustomEvent<Record<string, unknown>>).detail;
        if (detail?.requestId !== requestId) return;
        window.removeEventListener('tim-native-shell-response', onResponse);
        const point = detail.result as Record<string, unknown> | undefined;
        const latitude = Number(point?.latitude);
        const longitude = Number(point?.longitude);
        if (detail.status === 'ok' && Number.isFinite(latitude) && Number.isFinite(longitude)) {
          finishSuccess({
            coords: { latitude, longitude, accuracy: Number(point?.accuracy || 0) },
            timestamp: Date.now(),
          } as GeolocationPosition);
          return;
        }
        const error = detail.error as Record<string, unknown> | undefined;
        finishFailure({
          ...unavailable,
          code: error?.code === 'permission_denied' ? 1 : 2,
          message: String(error?.message || 'Ứng dụng chưa lấy được vị trí.'),
        } as GeolocationPositionError);
      };
      fallbackTimer = window.setTimeout(() => {
        window.removeEventListener('tim-native-shell-response', onResponse);
        finishFailure({ ...unavailable, code: 3, message: 'Ứng dụng lấy vị trí quá lâu.' } as GeolocationPositionError);
      }, timeout);
      window.addEventListener('tim-native-shell-response', onResponse);
      try {
        nativeShell.postMessage(JSON.stringify({
          bridgeVersion: 1,
          requestId,
          command: 'location',
          payload: {},
          origin: location.origin,
          sentAt: Date.now(),
        }));
      } catch (_) {
        window.removeEventListener('tim-native-shell-response', onResponse);
        finishFailure(unavailable);
      }
      return;
    }
    // Chrome may leave the permission bubble unanswered indefinitely. The
    // category flow must still reach the filtered list without coordinates.
    fallbackTimer = window.setTimeout(() => finishFailure({
      ...unavailable,
      code: 3,
      message: 'Định vị chưa phản hồi, tiếp tục không dùng vị trí.',
    } as GeolocationPositionError), 2000);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(finishSuccess, finishFailure, options);
      return;
    }
    finishFailure(unavailable);
  };
};

const loadPublicSettings = async () => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 4000);
  try {
    const settingsRequest = fetch('/api/v1/sites/massage/content/settings', { signal: controller.signal, credentials: 'same-origin' });
    const pageRequest = screen === 'home'
      ? fetch('/api/v1/sites/massage/content/pages/home', { signal: controller.signal, credentials: 'same-origin' })
      : Promise.resolve(null);
    const [settingsResponse,pageResponse] = await Promise.all([settingsRequest, pageRequest]);
    const payload = settingsResponse.ok ? await settingsResponse.json() : null;
    const pagePayload = pageResponse?.ok ? await pageResponse.json() : null;
    const items = Array.isArray(payload?.data?.items) ? payload.data.items : [];
    const profile = items.find((item: {key?:string}) => item.key === 'site-profile')?.value || {};
    const seo = items.find((item: {key?:string}) => item.key === 'seo')?.value || {};
    const publicImageUrl = (value: unknown) => {
      try { const url = new URL(String(value || ''), location.origin); return url.protocol === 'https:' || url.origin === location.origin ? url.href : ''; }
      catch { return ''; }
    };
    const homeBannerUrl = publicImageUrl(profile.homeBannerUrl);
    const partnerRegisterBannerUrl = publicImageUrl(profile.partnerRegisterBannerUrl);
    (window as Window & { TimMassagePublicProfile?: { homeBannerUrl?: string; partnerRegisterBannerUrl?: string } }).TimMassagePublicProfile = { homeBannerUrl, partnerRegisterBannerUrl };
    if (partnerRegisterBannerUrl) document.documentElement.style.setProperty('--tm-register-banner-image', `url(${JSON.stringify(partnerRegisterBannerUrl)})`);
    else document.documentElement.style.removeProperty('--tm-register-banner-image');
    window.dispatchEvent(new CustomEvent('tim:public-settings', { detail: { homeBannerUrl, partnerRegisterBannerUrl } }));
    if (homeBannerUrl) config.brand.hero = homeBannerUrl;
    if (typeof profile.name === 'string' && profile.name.trim()) config.brand.name = profile.name.trim();
    if (typeof profile.tagline === 'string') config.brand.tagline = profile.tagline.trim();
    if (typeof profile.logoUrl === 'string' && profile.logoUrl.startsWith('/')) config.brand.logo = `${location.origin}${profile.logoUrl}`;
    if (typeof seo.defaultTitle === 'string' && seo.defaultTitle.trim()) document.title = seo.defaultTitle.trim();
    if (typeof seo.defaultDescription === 'string') {
      let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.append(meta); }
      meta.content = seo.defaultDescription.trim();
    }
    const hero = pagePayload?.data?.document?.blocks?.find((block: {type?:string}) => block.type === 'hero')?.props || {};
    if (typeof hero.heading === 'string') config.brand.homeHero.heading = hero.heading.trim();
    if (typeof hero.description === 'string') config.brand.homeHero.description = hero.description.trim();
  } catch { /* CMS public settings are optional until the pilot is enabled. */ }
  finally { window.clearTimeout(timer); }
};

onMounted(async () => {
  // The installed WordPress plugin defines this compatibility entry point.
  // The Node/Vue host must expose the same contract before copied UI scripts
  // run; otherwise choosing a service mode stops before navigation.
  installLocationCompatibility();
  if (areaRoute) {
    const target = new URL('/chuyen-vien/', location.origin);
    target.searchParams.set('area', areaRoute);
    location.replace(target.href);
    return;
  }
  const surfaceBootFailure = (reason: unknown, stage: 'account_boot' | 'store_boot' = screen === 'store-dashboard' ? 'store_boot' : 'account_boot') => {
    const code = String(reason).includes('RENDER_EMPTY') ? 'SURFACE_RENDER_EMPTY' : 'SURFACE_ASSET_LOAD_FAILED';
    reportSurfaceDiagnostic(stage, code);
    const target = document.querySelector<HTMLElement>('.tm-screen');
    if (!target) return;
    if (!target.hidden && target.childElementCount > 0) {
      console.warn('Optional surface enhancement failed', reason);
      releaseNavigationBoot();
      releaseSurfaceBoot();
      return;
    }
    const appRoot = document.querySelector<HTMLElement>('.tim-makeup-app');
    releaseNavigationBoot();
    appRoot?.classList.remove('tm-surface-booting');
    appRoot?.querySelector('.tm-boot-veil')?.remove();
    if (screen === 'partner-profile') {
      appRoot?.classList.add('tm-profile-ui-ready');
      if (appRoot) delete appRoot.dataset.profileLoading;
    }
    console.error('Surface boot failed', reason);
    const message = 'Không thể tải nội dung. Vui lòng thử lại.';
    document.querySelector<HTMLElement>('.tm-loading')?.setAttribute('hidden','');
    target.hidden = false;
    target.innerHTML = `<div class="tm-alert" role="alert"><strong>Chưa tải được nội dung</strong><p>${message.replace(/[<>&]/g, (value) => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[value] || value))}</p><button class="tm-btn" type="button" data-local-retry>Thử lại</button></div>`;
    target.querySelector('[data-local-retry]')?.addEventListener('click', () => location.reload());
  };
  // Only the explicit route boot promise may replace the screen. Unrelated
  // browser/optional-script failures must not destroy an in-progress page.
  if (screen === 'area-index' || screen === 'home-massage') {
    await loadPublicSettings();
    document.body.className = 'tim-makeup-document tim-makeup-page tim-platform tim-brand-massage tim-unified-ui';
    if (screen === 'area-index') document.title = 'Chuyên viên gần bạn - Tìm Massage';
    releaseNavigationBoot();
    releaseSurfaceBoot();
    return;
  }
  // CMS settings and optional customer restoration are independent. Running
  // them together removes one full network round-trip from every public page.
  const [restoredCustomer] = await Promise.all([
    restoreCustomer().catch(() => null),
    loadPublicSettings().then(() => null),
  ]);
  const authSnapshot = customerAuthSnapshot();
  // A temporary /v1/me failure must never be treated as a deliberate sign-out.
  // Keep the existing local session long enough for the owner lookup to retry.
  const hasRecoverableSession = Boolean(restoredCustomer || authSnapshot.session);
  const customer = restoredCustomer || authSnapshot.profile;
  if (hasRecoverableSession) {
    if (screen === 'auth') {
      screen = 'account';
      config.screen = 'account';
    }
    config.loggedIn = true;
    config.userId = typeof customer?.id === 'number' ? customer.id : 1;
    config.userAvatar = typeof customer?.avatarUrl === 'string' ? customer.avatarUrl : '';
    config.userDisplayName = typeof customer?.displayName === 'string' ? customer.displayName : (typeof customer?.name === 'string' ? customer.name : '');
    config.userPhone = typeof customer?.phone === 'string' ? customer.phone : '';
    accountHref.value = '/ca-nhan/';
    let storeLookupSucceeded = false;
    try {
      const storesResponse = await customerRequest('/v1/sites/massage/owner/stores');
      const storesEnvelope = await storesResponse.json().catch(() => null);
      storeLookupSucceeded = storesResponse.ok && Array.isArray(storesEnvelope?.data?.items);
      config.hasStore = Boolean(storeLookupSucceeded && storesEnvelope.data.items.length);
    } catch {
      reportSurfaceDiagnostic('store_lookup', 'SURFACE_STORE_LOOKUP_FAILED');
    }
    accountLabel.value = storeLookupSucceeded && config.hasStore ? 'Cửa hàng' : 'Cá nhân';
    // Redirect to registration only after a successful empty lookup. A timeout
    // or a transient API failure must preserve the store screen for retry.
    if (screen === 'store-dashboard' && storeLookupSucceeded && !config.hasStore) {
      screen = 'partner-register';
      config.screen = 'partner-register' as typeof config.screen;
    }
  }
  if (screen === 'store-dashboard' && !hasRecoverableSession) {
    reportSurfaceDiagnostic('session_restore', 'SURFACE_SESSION_UNAVAILABLE');
    const target = new URL('/tai-khoan/', location.origin);
    target.searchParams.set('redirect_to', location.href);
    location.replace(target.href);
    return;
  }
  installLegacyUiAdapter();
  document.body.className = `tim-makeup-document tim-makeup-page tim-platform tim-brand-massage tim-unified-ui`;
  const root = document.querySelector('.tim-makeup-app');
  if (root instanceof HTMLElement) {
    root.classList.remove('tm-auth-mode','tm-account-mode');
    root.classList.add(screen === 'account' ? 'tm-account-mode' : screen === 'auth' ? 'tm-auth-mode' : `tm-${screen}-mode`);
    root.dataset.tmScreen = screen;
  }
  const runtime = document.createElement('script');
  runtime.type = 'application/json';
  runtime.className = 'tm-runtime-config';
  runtime.textContent = JSON.stringify(config);
  root?.prepend(runtime);
  document.querySelectorAll<HTMLImageElement>('.tm-desktop-brand-logo').forEach((image) => { image.src = config.brand.logo; image.alt = config.brand.name; });
  document.querySelectorAll<HTMLElement>('.tm-desktop-brand small').forEach((node) => { node.textContent = config.brand.tagline; });
  normalizeMassageCopy();
  if (root) new MutationObserver(normalizeMassageCopy).observe(root,{childList:true,subtree:true});
  const loadingGuard = window.setTimeout(() => {
    const target = document.querySelector<HTMLElement>('.tm-screen');
    const loading = document.querySelector<HTMLElement>('.tm-loading');
    if (!target || !target.hidden) return;
    reportSurfaceDiagnostic('render_timeout', 'SURFACE_BOOT_TIMEOUT');
    releaseNavigationBoot();
    releaseSurfaceBoot();
    if (loading) loading.hidden = true;
    target.hidden = false;
    target.innerHTML = '<div class="tm-alert"><strong>Chưa tải được nội dung</strong><p>Trang mất quá nhiều thời gian phản hồi.</p><button class="tm-btn" type="button" data-local-retry>Thử lại</button></div>';
    target.querySelector('[data-local-retry]')?.addEventListener('click', () => location.reload());
  }, 10000);
  if (screen === 'orders') {
    ['/platform-assets/orders.css?v=20261028-orders-summary-filter-1','/platform-assets/profile-booking.css?v=20261030-statusbar-1','/platform-assets/profile-booking-success.css?v=20261030-statusbar-1','/platform-assets/order-action-dialog.css','/platform-assets/snap-sheet.css?v=20261028-map-under-sheet-1'].forEach(loadStyle);
  }
  if (screen === 'partners') {
    loadStyle('/platform-assets/nearby-reference.css');
  }
  if (screen === 'chat') {
    [
      '/platform-assets/inbox-reference.css',
      '/platform-assets/chat-mobile.css',
      '/platform-assets/inbox-desktop-card.css',
      '/platform-assets/group-chat.css',
      '/platform-assets/quick-order.css',
    ].forEach((href) => loadStyleBefore(href, 'link[href*="/platform-assets/brand-components.css"]'));
  }
  if (screen === 'auth' || screen === 'account') {
    [
      '/platform-assets/auth-reference.css',
      '/platform-assets/registration-otp.css',
      '/platform-assets/account-reference.css',
      '/platform-assets/account-app-polish.css',
      '/platform-assets/account-personal-layout.css?v=20261101-store-id-minimal-1',
      '/platform-assets/account-rewards.css',
      '/platform-assets/personal-reference.css',
    ].forEach(loadStyle);
  }
  if (screen === 'home') {
    [
      '/platform-assets/home-reference.css',
      '/platform-assets/home-category-rail.css',
      '/platform-assets/random-booking-sheet.css',
      // The home renderer inserts the partner CTA asynchronously.  Its SVG
      // dimensions and CTA frame live in this stylesheet, so it is a home
      // dependency as well as a registration-page dependency.
      '/platform-assets/partner-register-banner.css',
    ].forEach(loadStyle);
  }
  if (screen === 'partner-register' || screen === 'account') loadStyle('/platform-assets/partner-register-banner.css');
  if (screen !== 'partner-profile') loadStyle('/platform-assets/footer-unified.css');
  if (screen === 'partner-profile') {
    [
      '/platform-assets/profile-reference.css',
      '/platform-assets/profile-pricing.css',
      '/platform-assets/pricing-settings.css',
      '/platform-assets/pricing-parity.css',
      '/platform-assets/profile-green-layout.css?v=20261029-profile-section-canvas-1',
      '/platform-assets/profile-readable-scale.css',
      '/platform-assets/profile-service-reference.css',
      '/platform-assets/profile-booking.css?v=20261030-statusbar-1',
      '/platform-assets/snap-sheet.css?v=20261028-map-under-sheet-1',
      '/platform-assets/profile-booking-success.css?v=20261030-statusbar-1',
      '/platform-assets/order-action-dialog.css',
      '/platform-assets/store.css',
      '/massage-assets/ad-booking-parity.css?v=20261028-booking-map-node-3',
    ].forEach(loadStyle);
  }
  if (screen === 'store-dashboard' || screen === 'partner-register') {
    ['/platform-assets/store.css','/platform-assets/store-config.css','/massage-assets/store-category-picker.css?v=20261030-chevron-1'].forEach(loadStyle);
  }
  // The home renderer is already usable as soon as app.js has painted the
  // first screen. Do not keep the boot veil over that screen while optional
  // rails, banners and visual polish are downloading.
  // `home-reference.js` owns the first render of `.tm-screen` and requires
  // `partner-register-banner.js` to be available synchronously. Keep this
  // tiny render path separate from visual enhancements: waiting for a render
  // before loading its renderer leaves the home screen permanently empty.
  const loadHomeRenderer = () => loadScript('/platform-assets/partner-register-banner.js', 'partner-register')
    .then(() => loadScript('/platform-assets/home-reference.js', 'home-reference'));
  const loadHomeEnhancements = () => loadSeries([
    ['/platform-assets/random-booking-sheet.js', 'random-booking-sheet'],
    ['/platform-assets/home-category-rail.js', 'home-category-rail'],
  ]);
  const boot = screen === 'map'
    ? ensureGoongAssets().then(() => loadSeries([
        ['/platform-assets/location-guard.js','location-guard'],
        ['/platform-assets/location-privacy.js','location-privacy'],
        ['/platform-assets/app.js?v=20261102-chat-support-identity-2', 'tim-platform-public-support-inbox-3'],
      ]))
    : screen === 'store-dashboard' || screen === 'partner-register'
    // Store configuration initializes a map synchronously.  It therefore
    // keeps the map runtime as a route dependency, while public profiles do
    // not pay this cost before their Node data is visible.
    ? ensureGoongAssets().then(() => loadSeries([
        ['/platform-assets/location-privacy.js','location-privacy'],
        ...(screen === 'partner-register' ? [['/platform-assets/registration-config.js?v=20261030-store-info-simplify-2','registration-config'] as [string,string]] : []),
        ['/platform-assets/store-config.js?v=20261030-registration-draft-media-1','store-config'],
        ['/massage-assets/store-category-picker.js?v=20261030-chevron-1','massage-store-category-picker'],
        ['/platform-assets/store-service-upload-visual.js','store-service-upload-visual'],
      ]))
    : screen === 'account'
    // The core renderer is enough to show a usable account page. Header,
    // rewards and registration widgets are visual enhancements; a CDN/cache
    // miss in one of them must not blank the entire Cửa hàng/Cá nhân screen.
    ? loadScript('/platform-assets/app.js?v=20261102-chat-support-identity-2', 'tim-platform-public-support-inbox-3')
        .then(waitForInitialRender)
        .then(() => {
          void loadSeries([
            ['/platform-assets/partner-register-banner.js','partner-register'],
            ['/platform-assets/account-header.js?v=20261101-location-tracking-resume-1','account-header'],
            ['/platform-assets/account-rewards.js','account-rewards'],
          ]).catch((error) => console.warn('Account enhancement failed', error));
        })
    : screen === 'orders'
    ? ensureGoongAssets()
        .catch((error) => console.warn('Order map runtime failed', error))
        .then(() => loadSeries([
          ['/platform-assets/snap-sheet.js','snap-sheet'],
          ['/platform-assets/order-contact.js?v=20261030-call-chat-routing-1','order-contact-policy'],
          ['/platform-assets/profile-booking.js?v=20261030-call-chat-routing-1','profile-booking-frame-1'],
          ['/platform-assets/orders.js?v=20261030-call-chat-routing-1','orders-summary-filter-1'],
        ]))
    : (screen === 'partner-profile'
        // Render the profile from Node data first.  Booking, media, location
        // and map enhancements are non-critical and must never keep the boot
        // veil on screen.
        ? loadScript('/platform-assets/app.js?v=20261102-chat-support-identity-2', 'tim-platform-public-support-inbox-3')
          .then(waitForInitialRender)
          .then(() => {
            void loadSeries([
              ['/platform-assets/chat-history.js','chat-history'],
              ['/platform-assets/pricing-icons.js','pricing-icons'],
              ['/platform-assets/pricing-media.js','pricing-media'],
              ['/platform-assets/pricing-settings.js','pricing-settings'],
              ['/platform-assets/snap-sheet.js','snap-sheet'],
              ['/platform-assets/order-contact.js?v=20261030-call-chat-routing-1','order-contact-policy'],
          ['/platform-assets/profile-booking.js?v=20261030-call-chat-routing-1','profile-booking-frame-1'],
              ['/platform-assets/profile-pricing-card.js?v=20261030-toggle-1','profile-pricing-card'],
              ['/platform-assets/location-guard.js','location-guard'],
              ['/platform-assets/nearby-reference.js','nearby-reference'],
            ]).catch((error) => console.warn('Profile enhancement failed', error));
            void ensureGoongAssets()
              .then(() => loadSeries([
                ['/platform-assets/profile-map-icon.js','profile-map-icon'],
                ['/platform-assets/profile-contact-scroll.js','profile-contact-scroll'],
                ['/platform-assets/location-privacy.js','location-privacy'],
                ['/platform-assets/profile-video-gallery.js','profile-video-gallery'],
                ['/platform-assets/profile-green-layout.js','profile-green-layout'],
                ['/unified-assets/profile-info-polish.js','unified-profile-info-polish'],
              ]))
              .catch((error) => console.warn('Profile map enhancement failed', error));
          })
        : screen === 'chat'
          ? loadSeries([
              ['/platform-assets/chat-history.js','chat-history'],
            ])
          : screen === 'partners'
            ? loadSeries([
                ['/platform-assets/location-guard.js','location-guard'],
                ['/platform-assets/nearby-reference.js','nearby-reference'],
              ])
          : Promise.resolve())
        .then(() => loadScript('/platform-assets/app.js?v=20261102-chat-support-identity-2', 'tim-platform-public-support-inbox-3'))
        .then(() => screen === 'home' ? undefined : waitForInitialRender())
        .then(() => {
          if (screen === 'chat') return loadSeries([
            ['/platform-assets/inbox-reference.js','inbox-reference'],
            ['/platform-assets/chat-mobile.js','chat-mobile'],
            ['/platform-assets/chat-image-delivery.js','chat-image-delivery'],
            ['/platform-assets/chat-call-audio.js','chat-call-audio'],
            ['/platform-assets/order-contact.js?v=20261030-call-chat-routing-1','order-contact-policy'],
            ['/platform-assets/chat-web-call.js?v=20261029-call-foundation-3','chat-web-call'],
            ['/platform-assets/chat-call-lifecycle.js?v=20261029-call-lifecycle-2','chat-call-lifecycle'],
            ['/platform-assets/chat-call-telemetry.js','chat-call-telemetry'],
            ['/platform-assets/chat-phone-dial.js?v=20261030-remove-store-refresh-1','chat-phone-dial'],
            ['/platform-assets/group-chat.js','group-chat'],
            ['/platform-assets/quick-order.js','quick-order'],
          ]);
          if (screen === 'auth') return loadSeries([
            ['/platform-assets/auth-brand.js','auth-brand'],
          ]);
        })
        ;
  const initialRender = screen === 'home'
    ? boot.then(loadHomeRenderer).then(waitForInitialRender)
    : boot.then(waitForInitialRender);
  const releaseInitialSurface = () => {
    window.clearTimeout(loadingGuard);
    releaseNavigationBoot();
    releaseSurfaceBoot();
  };
  if (screen === 'home') {
    // First content is visible before non-essential home enhancements load.
    void initialRender
      .then(() => {
        releaseInitialSurface();
        // These scripts enrich an already rendered home page. Their failure
        // must never replace usable content with the full-screen error state.
        void Promise.all([
          loadScript('/platform-assets/footer-unified.js', 'footer-unified'),
          loadScript('/massage-assets/brand.js', 'tim-massage-ui-2.0.154'),
          loadHomeEnhancements(),
        ]).catch((error) => console.warn('Home enhancement failed', error));
      })
      .catch((error) => {
        window.clearTimeout(loadingGuard);
        surfaceBootFailure(error, 'account_boot');
      });
    return;
  }
  void initialRender
    .then(() => loadScript('/platform-assets/footer-unified.js', 'footer-unified'))
    .then(() => loadScript('/massage-assets/brand.js', 'tim-massage-ui-2.0.154'))
    .then(() => screen === 'auth'
      ? loadScript('/massage-assets/auth-social-polish.js', 'tim-massage-auth-social-polish')
      : undefined)
    .then(() => screen === 'auth' ? installLegacyCustomerSocialAuth() : undefined)
    .then(() => screen === 'chat' ? loadSeries([
      ['/massage-assets/chat-image-optimize.js', 'tim-massage-chat-image-optimize'],
    ]) : undefined)
    .then(() => screen === 'store-dashboard' || screen === 'partner-register'
      ? loadScript('/massage-assets/store-polish.js', 'tim-massage-store-polish')
      : undefined)
    .then(() => {
      releaseInitialSurface();
    })
    .catch((error) => {
      window.clearTimeout(loadingGuard);
      surfaceBootFailure(error);
    });
});
</script>

<template>
  <div class="tim-makeup-app tim-platform-app tim-brand-massage tm-surface-booting" :class="[routeClass, {'tm-area-entry-route': screen === 'area-index'}]" :data-tm-screen="screen" :data-profile-contact-disabled="screen === 'partner-profile' ? '1' : undefined" data-tim-brand="massage">
    <a class="tm-skip" href="#tm-main">Bỏ qua đến nội dung</a>
    <header class="tm-desktop-header" aria-label="Điều hướng máy tính">
      <a class="tm-desktop-brand" href="/" aria-label="Tìm Massage — Trang chủ"><img class="tm-desktop-brand-logo" src="/massage-assets/logo-header.png" alt="Tìm Massage"><small>Thư giãn hơn, gần bạn hơn</small></a>
      <nav class="tm-desktop-links" aria-label="Điều hướng chính trên máy tính"><a href="/" :aria-current="current('home')">Trang chủ</a><a href="/chuyen-vien/" :aria-current="current('partners')">Chuyên viên</a><a href="/don-hang/" :aria-current="current('orders')">Đơn hàng</a><a href="/tin-nhan/" :aria-current="current('chat')">Chat</a></nav>
      <div class="tm-desktop-actions"><button class="tm-desktop-language" type="button" aria-label="Chọn ngôn ngữ"><span aria-hidden="true">🇻🇳</span></button><a class="tm-desktop-account" :href="accountHref"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg><span>{{ accountLabel }}</span></a><a class="tm-desktop-partner" href="/quan-ly-gian-hang/"><span>Đăng ký gian hàng</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg></a></div>
    </header>
    <div class="tm-shell">
      <header v-if="!['partner-profile','partners','auth','account','area-index','orders','chat','store-dashboard','partner-register'].includes(screen)" class="tm-header"><a class="hr-brand" href="/" aria-label="Tìm Massage — Trang chủ"><img src="/massage-assets/logo-header.png" alt="Tìm Massage" width="190" height="42"></a></header>
      <main id="tm-main" class="tm-main" tabindex="-1"><AreaIndexPage v-if="screen === 'area-index'"/><MassageAtHomePage v-else-if="screen === 'home-massage'"/><template v-else><div class="tm-boot-veil" role="status" aria-live="polite"><div class="tm-boot-cover"></div><div class="tm-boot-card"><span></span><span></span><span></span></div><div class="tm-boot-card tm-boot-card-short"><span></span><span></span></div><span class="screen-reader-text">Đang tải nội dung</span></div><div class="tm-loading tm-legacy-loading" hidden aria-hidden="true"></div><div class="tm-screen" hidden></div><noscript><div class="tm-alert">Bạn cần bật JavaScript để sử dụng đầy đủ Tìm Massage.</div></noscript></template></main>
      <nav v-if="screen !== 'area-index'" id="tm-global-footer" class="tm-nav" aria-label="Điều hướng chính">
        <a href="/" data-nav="home" :aria-current="current('home')"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3.5 10.4 12 3.5l8.5 6.9v9a1.6 1.6 0 0 1-1.6 1.6H15v-6.2H9V21H5.1a1.6 1.6 0 0 1-1.6-1.6v-9Z"/></svg><span>Trang chủ</span></a>
        <a href="/don-hang/" data-nav="orders" :aria-current="current('orders')"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="5" y="4.5" width="14" height="16" rx="2.5"/><path d="M8.5 9h7m-7 4h7m-7 4h4"/></svg><span>Đơn hàng</span></a>
        <a href="/tin-nhan/" data-nav="chat" :aria-current="current('chat')"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 11.5a9 9 0 0 1-9 9 10 10 0 0 1-4-.9L3 21l1.4-4.7A9 9 0 1 1 21 11.5Z"/></svg><span>Chat</span></a>
        <a :href="accountHref" data-nav="account" :aria-current="screen === 'auth' || screen === 'account' ? 'page' : undefined"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="7" r="4"/><path d="M4 21v-1a8 6 0 0 1 16 0v1"/></svg><span>{{ accountLabel }}</span></a>
      </nav>
    </div>
  </div>
</template>

<style>
.tim-makeup-app.tm-surface-booting .tm-screen,
.tim-makeup-app.tm-surface-booting .tm-shell>.tm-header{visibility:hidden!important}
.tim-makeup-app:not(.tm-navigation-ready) .tm-desktop-header,
.tim-makeup-app:not(.tm-navigation-ready) #tm-global-footer{visibility:hidden!important}
.tim-makeup-app .tm-loading.tm-legacy-loading{display:none!important}
.tim-makeup-app.tm-profile-route{font-weight:300}
.tim-makeup-app .tm-boot-veil{position:absolute;z-index:8;inset:0 0 auto;display:grid;gap:14px;min-height:100dvh;padding:0 16px 96px;background:#f4faf7;box-sizing:border-box;pointer-events:none}
.tim-makeup-app .tm-boot-cover{height:clamp(210px,48vw,310px);margin:0 -16px;background:linear-gradient(100deg,#e4f1ec 20%,#f7fbf9 40%,#e4f1ec 60%);background-size:220% 100%;animation:tmBootShimmer 1.05s infinite}
.tim-makeup-app .tm-boot-card{display:grid;gap:10px;padding:18px;border:1px solid #dce9e4;border-radius:18px;background:#fff}
.tim-makeup-app .tm-boot-card span{display:block;height:15px;border-radius:8px;background:linear-gradient(100deg,#e8f2ee 20%,#f8fbfa 40%,#e8f2ee 60%);background-size:220% 100%;animation:tmBootShimmer 1.05s infinite}
.tim-makeup-app .tm-boot-card span:nth-child(1){width:46%;height:22px}.tim-makeup-app .tm-boot-card span:nth-child(2){width:76%}.tim-makeup-app .tm-boot-card span:nth-child(3){width:58%}.tim-makeup-app .tm-boot-card-short{grid-template-columns:1fr 1fr}.tim-makeup-app .tm-boot-card-short span{width:100%!important;height:92px!important}
@keyframes tmBootShimmer{to{background-position:-220% 0}}
@media(min-width:800px){.tim-makeup-app .tm-boot-veil{padding-inline:28px}.tim-makeup-app .tm-boot-cover{margin-inline:-28px;height:min(38vw,430px)}.tim-makeup-app .tm-boot-card,.tim-makeup-app .tm-boot-card-short{width:min(1180px,100%);margin-inline:auto}}
</style>
